import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import NotFound from '@/pages/NotFound';
import { workCases } from '@/mocks/work';
import useLightReveal from '@/hooks/useLightReveal';

export default function WorkDetailPage() {
  const { slug = '' } = useParams();
  const ref = useRef<HTMLElement>(null);
  useLightReveal(ref, slug);

  const index = workCases.findIndex((c) => c.slug === slug);
  if (index === -1) return <NotFound />;
  const c = workCases[index];
  const next = workCases[(index + 1) % workCases.length];

  return (
    <>
      <title>{`${c.title} | AUREN Work`}</title>
      <meta name="description" content={`${c.brief} An illustrative AUREN engagement in ${c.discipline.toLowerCase()}.`} />
      <Navbar />
      <main ref={ref} className="bg-background-50 pt-14">
        <article>
          <header className="wrap pt-10 md:pt-14">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-foreground-950/15 pb-4">
              <nav aria-label="Breadcrumb" className="label-xs text-foreground-600">
                <Link to="/work" className="hover:text-foreground-950 link-line pb-1 cursor-pointer">Work</Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-950">{c.category}</span>
              </nav>
              <Link to="/work" className="label-xs text-foreground-950 link-line pb-1 cursor-pointer whitespace-nowrap">&larr; Back to work</Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-10 pt-14 md:pt-24 pb-12 md:pb-16">
              <div className="lg:col-span-9" data-fade>
                <h1 className="font-heading font-light uppercase leading-[1] tracking-[0.02em] text-foreground-950 text-[clamp(36px,6vw,88px)] max-w-[15ch]">{c.title}</h1>
                <p className="mt-8 text-[16px] leading-[1.65] text-foreground-700 max-w-[520px]">{c.brief}</p>
              </div>
              <dl className="lg:col-span-3 flex flex-col gap-6 lg:pt-3" data-fade>
                <div className="border-t border-foreground-950/15 pt-3">
                  <dt className="label-xs text-foreground-600">Discipline</dt>
                  <dd className="mt-2 text-[15px] text-foreground-950">{c.discipline}</dd>
                </div>
                <div className="border-t border-foreground-950/15 pt-3">
                  <dt className="label-xs text-foreground-600">Focus</dt>
                  <dd className="mt-2 text-[15px] text-foreground-950">{c.focus}</dd>
                </div>
                <span className="label-xs text-accent-700">Illustrative engagement</span>
              </dl>
            </div>
          </header>

          <div className="wrap">
            <div className="w-full aspect-[4/3] md:aspect-[16/9] overflow-hidden bg-background-200" data-clip>
              <img src={c.cover} alt={`${c.title} — illustrative engagement cover`} title={c.title} className="w-full h-full object-cover object-center" />
            </div>
          </div>

          <div className="wrap py-20 md:py-32">
            <div className="flex flex-col gap-14 md:gap-20">
              {c.sections.map((s, i) => (
                <section key={s.title} className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-4" data-fade>
                  <h2 className="lg:col-span-4 label-xs text-foreground-950 flex gap-4">
                    <span className="text-foreground-600">{String(i + 1).padStart(2, '0')}</span>
                    {s.title}
                  </h2>
                  <p className="lg:col-start-6 lg:col-span-6 text-[16px] leading-[1.7] text-foreground-800 max-w-[620px]">{s.text}</p>
                </section>
              ))}
            </div>
          </div>

          <div className="wrap pb-20 md:pb-32">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {c.gallery.map((g, i) => (
                <div key={g} className="w-full aspect-[4/3] overflow-hidden bg-background-200" data-fade>
                  <img src={g} alt={`${c.title} — supporting image ${i + 1}`} title={c.title} loading="lazy" className="w-full h-full object-cover object-center" />
                </div>
              ))}
            </div>
            <p className="label-xs text-foreground-600 mt-6 max-w-xl leading-relaxed">Illustrative composite of the way we work &mdash; not a specific client or result.</p>
          </div>
        </article>

        <nav aria-label="Case navigation" className="border-t border-foreground-950/15">
          <div className="wrap py-14 md:py-20 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <Link to={`/work/${next.slug}`} className="group flex items-end justify-between gap-8 flex-1 max-w-[760px] cursor-pointer">
              <span>
                <span className="label-xs text-foreground-600">Next engagement &rarr;</span>
                <span className="block mt-3 font-heading font-light uppercase tracking-[0.02em] leading-[1.05] text-foreground-950 group-hover:text-primary-500 transition-colors text-[clamp(24px,3.4vw,44px)]">{next.title}</span>
              </span>
              <span className="circle-arrow inv-dark text-foreground-950"><i className="ri-arrow-right-line" /></span>
            </Link>
            <Link to="/work" className="label-xs text-foreground-950 link-line pb-1 cursor-pointer whitespace-nowrap self-start md:self-end">Back to all work</Link>
          </div>
        </nav>
      </main>
      <Footer />
    </>
  );
}